import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Pencil, Plus, User as UserIcon, Users } from "lucide-react";
import { Button } from "../../base/button/button";
import { ACTION_TYPE_ICONS, STEP_FIELD_ICONS } from "./stepFieldIcons";
import { AssigneeDisplay } from "./AssigneeDisplay";
import { FieldSectionLabel } from "./FieldSectionLabel";
import { EditableInputsSection } from "./EditableInputsSection";
import { InfoHint } from "../info-hint/info-hint";
import { SuccessorSummary } from "./SuccessorSummary";
import { buildAssigneePatch } from "./assigneeSubmit";
import { useStepSuggestionInfoMap } from "./useStepSuggestionInfoMap";
import { DecisionRulesSection } from "../decision-table/DecisionRulesSection";
import { DecisionTableDialog } from "../decision-table/DecisionTableDialog";
import { useDebouncedFieldUpdate } from "../../../hooks/useDebouncedFieldUpdate";
import { FieldIssueFrame } from "./FieldIssueFrame";
import { invalidControlAria, useFieldIssueFrames } from "./stepFieldIssues";
// Reads a string id out of any of the slot entry shapes that can appear after
// population. Suggestion refs return undefined (no entity to display in the
// form) and are filtered out by the caller.
function extractId(v) {
    if (typeof v === "string") return v;
    if ("type" in v) return v.type === "entity" ? v.id : undefined;
    return v._id;
}
function extractIds(arr) {
    if (!arr) return [];
    const out = [];
    for (const v of arr){
        const id = extractId(v);
        if (id) out.push(id);
    }
    return out;
}
function diffAdded(prev, next) {
    if (!next) return [];
    const prevSet = new Set(prev);
    return next.filter((id)=>!prevSet.has(id));
}
// orgFunctions/systems are EntityOrSuggestionRef[]. Carry the FULL union ref for newly
// added entries (entity AND suggestion) so eager materialization can bind a
// draft node to a pending suggestion. Keyed by `type:id` since a suggestion id
// and an entity id live in separate spaces.
const refKey = (r)=>`${r.type}:${r.id}`;
function diffAddedRefs(prev, next) {
    if (!next) return [];
    const prevSet = new Set(prev.map(refKey));
    return next.filter((r)=>!prevSet.has(refKey(r)));
}
// Filters an entity id out of a successor list, handling both plain-id
// (persons/externalContacts) and union-ref (orgFunctions/systems) entries.
function removeId(list, id) {
    return list.filter((v)=>(typeof v === "string" ? v : v.id) !== id);
}
const EMPTY_SUCCESSOR_VALUE = {
    persons: [],
    orgFunctions: [],
    systems: [],
    externalContacts: [],
    successorStatus: ""
};
function refEntityIds(refs) {
    return refs.filter((r)=>r.type === "entity").map((r)=>r.id);
}
// Resolved entity ids already chosen — handed to the picker as exclusions.
// Suggestion refs are dropped (they have no pickable entity yet).
function excludeIdsOf(value) {
    return {
        persons: value.persons,
        orgFunctions: refEntityIds(value.orgFunctions),
        systems: refEntityIds(value.systems),
        externalContacts: value.externalContacts
    };
}
/**
 * Shared submit pipeline for the task-level and per-rule successor dialogs:
 * merge the picker's per-field value over the current one, then route to
 * exactly one of three writes.
 *
 * `onMarkTerminal` / `onAddSteps` are passed only by draft hosts that
 * materialize downstream steps; when they are absent every write falls through
 * to `patchMerged`.
 *
 * 1. Terminal pick (NONE / UNCLEAR) while materialized children exist: hand
 *    off to `onMarkTerminal` so the children are removed alongside the status
 *    change. Leaving them would paint both an end marker AND their edges on
 *    the map, contradicting the declared terminal state.
 * 2. Newly-added entities: hand off to `onAddSteps` (the backend
 *    endpoint), which atomically merges the successors AND materializes a
 *    downstream draft assignment per new entity. We must NOT also patch
 *    metadata — a parallel PATCH would race ahead and populate the
 *    successors, after which the backend's idempotency check would skip step
 *    creation.
 * 3. Otherwise: metadata-only fallback via `patchMerged` (recording mode, or
 *    status-only changes that don't add entities).
 */ function submitSuccessorUpdate({ data, prev, onMarkTerminal, onAddSteps, patchMerged }) {
    const merged = {
        persons: data.persons ?? prev.persons,
        orgFunctions: data.orgFunctions ?? prev.orgFunctions,
        systems: data.systems ?? prev.systems,
        externalContacts: data.externalContacts ?? prev.externalContacts,
        successorStatus: data.successorStatus ?? prev.successorStatus
    };
    const hasConcreteStatus = merged.successorStatus !== "NONE" && merged.successorStatus !== "UNCLEAR";
    const pickedTerminal = data.successorStatus === "NONE" || data.successorStatus === "UNCLEAR";
    const hadMaterializedChildren = prev.persons.length > 0 || prev.orgFunctions.length > 0 || prev.systems.length > 0 || prev.externalContacts.length > 0;
    if (onMarkTerminal && pickedTerminal && hadMaterializedChildren) {
        void onMarkTerminal(data.successorStatus);
        return;
    }
    const newlyAdded = {
        persons: diffAdded(prev.persons, data.persons),
        // orgFunctions/systems carry the EntityOrSuggestionRef union — a newly-added
        // suggestion ref eagerly materializes a draft node bound to the pending
        // suggestion (approval rewrites it later).
        orgFunctions: diffAddedRefs(prev.orgFunctions, data.orgFunctions),
        systems: diffAddedRefs(prev.systems, data.systems),
        externalContacts: diffAdded(prev.externalContacts, data.externalContacts)
    };
    const hasNew = (newlyAdded.persons?.length ?? 0) > 0 || (newlyAdded.orgFunctions?.length ?? 0) > 0 || (newlyAdded.systems?.length ?? 0) > 0 || (newlyAdded.externalContacts?.length ?? 0) > 0;
    if (onAddSteps && hasConcreteStatus && hasNew) {
        void onAddSteps(newlyAdded);
        return;
    }
    patchMerged(merged);
}
function currentSuccessorValue(successors) {
    return {
        persons: extractIds(successors?.persons),
        // orgFunctions/systems carry the EntityOrSuggestionRef union on both the
        // populated and unpopulated successor shapes — no conversion needed.
        orgFunctions: successors?.orgFunctions ?? [],
        systems: successors?.systems ?? [],
        externalContacts: extractIds(successors?.externalContacts),
        successorStatus: successors?.successorStatus ?? ""
    };
}
// ---------------------------------------------------------------------------
// Shared small UI blocks
// ---------------------------------------------------------------------------
function ActionTypeToggle({ selected, onSelect, labelFor, hint }) {
    const buttons = [
        {
            value: "task",
            Icon: ACTION_TYPE_ICONS.task
        },
        {
            value: "decision",
            Icon: ACTION_TYPE_ICONS.decision
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-1.5 p-1 bg-white border border-line rounded-xl",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-1 gap-1.5 items-center",
                children: buttons.map(({ value, Icon })=>{
                    const isActive = selected === value;
                    return /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>onSelect(value),
                        className: `relative flex flex-1 items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-semibold ${isActive ? "bg-[#fff9e9] text-black" : "text-gray-5"}`,
                        children: [
                            /*#__PURE__*/ _jsx(Icon, {
                                className: "w-3.5 h-3.5"
                            }),
                            labelFor(value),
                            isActive && /*#__PURE__*/ _jsx("div", {
                                "aria-hidden": "true",
                                className: "absolute border border-border-7 border-solid inset-0 pointer-events-none rounded-lg"
                            })
                        ]
                    }, value);
                })
            }),
            hint && /*#__PURE__*/ _jsx(InfoHint, {
                content: hint,
                className: "mr-1"
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function EditableStepDetailContent({ step, mode = "draft", onPatchStep, successorActions, assigneeLookup, eventTypes, processSteps, renderSuccessorPicker, renderAssigneePicker, fieldSlots, canAddInput, fieldIssues, invalidRuleIndexes }) {
    const { t } = useTranslation();
    const frameFor = useFieldIssueFrames(fieldIssues);
    const taskDescriptionFrame = frameFor("task.taskDescription");
    const outputFrame = frameFor("task.output");
    const isTask = step.actionType === "task";
    const isDecision = step.actionType === "decision";
    const showActionTypeToggle = mode === "draft";
    const graphActions = mode === "draft" ? successorActions : undefined;
    // --- Dialog state (local) ---
    const [showDecisionTableModal, setShowDecisionTableModal] = useState(false);
    const [showSuccessorDialog, setShowSuccessorDialog] = useState(false);
    const [showAssigneeDialog, setShowAssigneeDialog] = useState(false);
    /**
   * Rule whose successors the user is editing via the decision-rules table
   * Successors cell. `null` when no per-rule dialog is open.
   */ const [editingRuleId, setEditingRuleId] = useState(null);
    // --- Derived data ---
    const recordedByName = step.recordedBy ? `${step.recordedBy.firstName ?? ""} ${step.recordedBy.lastName ?? ""}`.trim() || step.recordedBy.email : null;
    const successorValue = useMemo(()=>currentSuccessorValue(step.task?.successors), [
        step.task?.successors
    ]);
    // No second argument: the editor floods the draft-wide suggestion set onto
    // every step, so each step's own `entitySuggestions` already covers its refs.
    const suggestionInfoById = useStepSuggestionInfoMap(step);
    const editingRule = useMemo(()=>{
        if (!editingRuleId) return null;
        return step.rules?.find((r)=>r._id && String(r._id) === editingRuleId) ?? null;
    }, [
        editingRuleId,
        step.rules
    ]);
    const editingRuleSuccessorValue = useMemo(()=>currentSuccessorValue(editingRule?.successors), [
        editingRule
    ]);
    // --- Debounced text fields ---
    const taskDescField = useDebouncedFieldUpdate({
        value: step.task?.taskDescription ?? "",
        onCommit: (next)=>void onPatchStep({
                task: {
                    ...step.task ?? {},
                    taskDescription: next,
                    output: step.task?.output,
                    successors: step.task?.successors
                }
            })
    });
    const outputField = useDebouncedFieldUpdate({
        value: step.task?.output ?? "",
        onCommit: (next)=>void onPatchStep({
                task: {
                    ...step.task ?? {},
                    taskDescription: step.task?.taskDescription,
                    output: next,
                    successors: step.task?.successors
                }
            })
    });
    // --- Handlers ---
    const handleSuccessorSubmit = useCallback((data)=>{
        submitSuccessorUpdate({
            data,
            prev: successorValue,
            onMarkTerminal: graphActions?.markTerminal,
            onAddSteps: graphActions?.addSteps,
            patchMerged: (merged)=>void onPatchStep({
                    task: {
                        taskDescription: step.task?.taskDescription,
                        output: step.task?.output,
                        successors: merged
                    }
                })
        });
        setShowSuccessorDialog(false);
    }, [
        successorValue,
        step.task,
        onPatchStep,
        graphActions
    ]);
    const handleRemoveSuccessor = useCallback((type, id)=>{
        if (graphActions) {
            // Draft mode: disconnect the downstream node (drop the edge, keep the
            // node). The backend's recompute on disconnect updates parent metadata
            // from the graph, so skip the metadata PATCH entirely — otherwise we'd
            // push a redundant undo entry whose effect matches the reconnect on undo.
            void graphActions.removeStep(type, id);
            return;
        }
        void onPatchStep({
            task: {
                taskDescription: step.task?.taskDescription,
                output: step.task?.output,
                successors: {
                    ...successorValue,
                    [type]: removeId(successorValue[type], id)
                }
            }
        });
    }, [
        successorValue,
        step.task,
        onPatchStep,
        graphActions
    ]);
    const handleClearSuccessorStatus = useCallback(()=>{
        void onPatchStep({
            task: {
                taskDescription: step.task?.taskDescription,
                output: step.task?.output,
                successors: {
                    persons: successorValue.persons,
                    orgFunctions: successorValue.orgFunctions,
                    systems: successorValue.systems,
                    externalContacts: successorValue.externalContacts,
                    successorStatus: ""
                }
            }
        });
    }, [
        successorValue,
        step.task,
        onPatchStep
    ]);
    const handleAssigneeSubmit = useCallback((data)=>{
        const patch = buildAssigneePatch(data);
        if (patch) void onPatchStep(patch);
        setShowAssigneeDialog(false);
    }, [
        onPatchStep
    ]);
    const handleAssigneeChange = useCallback((patch)=>{
        void onPatchStep(patch);
    }, [
        onPatchStep
    ]);
    const handleSaveDecisionRules = useCallback((rules)=>{
        void onPatchStep({
            rules
        });
        setShowDecisionTableModal(false);
    }, [
        onPatchStep
    ]);
    const handleRuleSuccessorSubmit = useCallback((data)=>{
        if (!editingRuleId) return;
        submitSuccessorUpdate({
            data,
            prev: editingRuleSuccessorValue,
            onMarkTerminal: graphActions && ((status)=>graphActions.markRuleTerminal(editingRuleId, status)),
            onAddSteps: graphActions && ((body)=>graphActions.addRuleSteps(editingRuleId, body)),
            // Metadata-only fallback: write the merged successors onto the rule.
            patchMerged: (merged)=>{
                const nextRules = (step.rules ?? []).map((rule)=>{
                    const id = rule._id;
                    if (!id || String(id) !== editingRuleId) return rule;
                    return {
                        ...rule,
                        successors: merged
                    };
                });
                void onPatchStep({
                    rules: nextRules
                });
            }
        });
        setEditingRuleId(null);
    }, [
        editingRuleId,
        editingRuleSuccessorValue,
        step.rules,
        onPatchStep,
        graphActions
    ]);
    const handleRemoveRuleSuccessor = useCallback((ruleId, type, entityId)=>{
        if (graphActions) {
            // Draft mode: disconnect the downstream node (drop the rule's edge,
            // keep the node). Backend recompute updates `rule.successors` from the
            // graph — skip the metadata patch to avoid a redundant undo entry.
            void graphActions.removeRuleStep(ruleId, type, entityId);
            return;
        }
        const nextRules = (step.rules ?? []).map((rule)=>{
            const id = rule._id;
            if (!id || String(id) !== ruleId) return rule;
            const r = rule;
            const prev = r.successors;
            if (!prev) return rule;
            const prevList = prev[type] ?? [];
            const nextList = prevList.filter((v)=>{
                const id = extractId(v);
                return id !== entityId;
            });
            return {
                ...rule,
                successors: {
                    ...prev,
                    [type]: nextList
                }
            };
        });
        void onPatchStep({
            rules: nextRules
        });
    }, [
        step.rules,
        onPatchStep,
        graphActions
    ]);
    const handleClearRuleSuccessorStatus = useCallback((ruleId)=>{
        const nextRules = (step.rules ?? []).map((rule)=>{
            const id = rule._id;
            if (!id || String(id) !== ruleId) return rule;
            const r = rule;
            const prev = r.successors ?? {};
            return {
                ...rule,
                successors: {
                    ...prev,
                    successorStatus: ""
                }
            };
        });
        void onPatchStep({
            rules: nextRules
        });
    }, [
        step.rules,
        onPatchStep
    ]);
    // --- Render ---
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "min-w-0 flex-1",
                                children: /*#__PURE__*/ _jsxs(FieldIssueFrame, {
                                    ...frameFor("assignedTo"),
                                    children: [
                                        /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                            icon: UserIcon,
                                            slot: fieldSlots?.assignee,
                                            children: t("processFlow.assignedTo")
                                        }),
                                        renderAssigneePicker ? renderAssigneePicker({
                                            assignedTo: step.assignedTo,
                                            assignedToModel: step.assignedToModel
                                        }, handleAssigneeChange) : /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            onClick: ()=>setShowAssigneeDialog(true),
                                            className: "group relative w-full text-left bg-surface-subtle hover:bg-muted rounded-sm px-2 py-1.5 transition-colors",
                                            children: [
                                                /*#__PURE__*/ _jsx(Pencil, {
                                                    className: "absolute top-1.5 right-1.5 w-3 h-3 text-fg-subtle opacity-0 group-hover:opacity-100"
                                                }),
                                                /*#__PURE__*/ _jsx(AssigneeDisplay, {
                                                    assignedTo: step.assignedTo,
                                                    assignedToModel: step.assignedToModel,
                                                    entitySuggestions: step.entitySuggestions,
                                                    assigneeLookup: assigneeLookup,
                                                    t: t
                                                })
                                            ]
                                        })
                                    ]
                                })
                            }),
                            recordedByName && /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                        icon: UserIcon,
                                        children: t("processFlow.recordedBy")
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm text-foreground bg-surface-subtle px-2 py-1.5 rounded-sm truncate",
                                        children: recordedByName
                                    })
                                ]
                            })
                        ]
                    }),
                    fieldSlots?.whoElseDoesThis && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                icon: Users,
                                hint: t(mode === "draft" ? "processFlow.whoElseDoesThisDescriptionEditor" : "processFlow.whoElseDoesThisDescription"),
                                children: t("processFlow.whoElseDoesThis")
                            }),
                            fieldSlots.whoElseDoesThis
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FieldIssueFrame, {
                        ...frameFor("inputs"),
                        children: /*#__PURE__*/ _jsx(EditableInputsSection, {
                            inputs: step.inputs,
                            currentStepId: step._id,
                            mode: mode,
                            eventTypes: eventTypes,
                            processSteps: processSteps,
                            canAdd: canAddInput,
                            onChange: (next)=>void onPatchStep({
                                    inputs: next
                                })
                        })
                    }),
                    showActionTypeToggle && /*#__PURE__*/ _jsx(FieldIssueFrame, {
                        ...frameFor("actionType"),
                        children: /*#__PURE__*/ _jsx(ActionTypeToggle, {
                            selected: step.actionType,
                            onSelect: (v)=>void onPatchStep({
                                    actionType: v
                                }),
                            labelFor: (v)=>t(`processDraft.actionType.${v}`),
                            hint: t("processDraft.actionTypeHint")
                        })
                    }),
                    isTask && /*#__PURE__*/ _jsxs(FieldIssueFrame, {
                        ...taskDescriptionFrame,
                        children: [
                            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                icon: STEP_FIELD_ICONS.taskDescription,
                                slot: fieldSlots?.taskDescription,
                                children: t("processFlow.taskDescription")
                            }),
                            /*#__PURE__*/ _jsx("textarea", {
                                value: taskDescField.value,
                                onChange: (e)=>taskDescField.onChange(e.target.value),
                                onBlur: taskDescField.flush,
                                rows: 3,
                                className: "w-full text-sm border border-line rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-info-solid aria-invalid:border-destructive",
                                placeholder: t("processFlow.taskDescription"),
                                ...invalidControlAria(taskDescriptionFrame)
                            })
                        ]
                    }),
                    isDecision && /*#__PURE__*/ _jsxs(FieldIssueFrame, {
                        ...frameFor("rules"),
                        children: [
                            fieldSlots?.rules,
                            step.rules && step.rules.length > 0 ? /*#__PURE__*/ _jsx(DecisionRulesSection, {
                                rules: step.rules,
                                t: t,
                                onEditRuleSuccessors: setEditingRuleId,
                                onRemoveRuleSuccessor: handleRemoveRuleSuccessor,
                                onClearRuleSuccessorStatus: handleClearRuleSuccessorStatus,
                                assigneeLookup: assigneeLookup,
                                suggestionInfoById: suggestionInfoById,
                                invalidRuleIndexes: invalidRuleIndexes
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-muted-foreground italic mb-1.5",
                                children: t("processFlow.editable.noDecisionRules")
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "ghost",
                                size: "small",
                                className: "mt-1.5 w-full h-7 text-muted-foreground hover:text-fg-secondary hover:bg-muted",
                                onClick: ()=>setShowDecisionTableModal(true),
                                "data-field-issue-focus": "",
                                children: [
                                    /*#__PURE__*/ _jsx(Pencil, {
                                        className: "w-3.5 h-3.5 mr-1"
                                    }),
                                    t("processFlow.editable.editDecisionRules")
                                ]
                            })
                        ]
                    }),
                    fieldSlots?.helpers && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                icon: STEP_FIELD_ICONS.helpers,
                                hint: t("processFlow.helpersHint"),
                                children: t("processFlow.helpers")
                            }),
                            fieldSlots.helpers
                        ]
                    }),
                    isTask && /*#__PURE__*/ _jsxs(FieldIssueFrame, {
                        ...outputFrame,
                        children: [
                            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                icon: STEP_FIELD_ICONS.output,
                                slot: fieldSlots?.output,
                                children: t("processFlow.output")
                            }),
                            /*#__PURE__*/ _jsx("textarea", {
                                value: outputField.value,
                                onChange: (e)=>outputField.onChange(e.target.value),
                                onBlur: outputField.flush,
                                rows: 2,
                                className: "w-full text-sm border border-line rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-info-solid aria-invalid:border-destructive",
                                placeholder: t("processFlow.output"),
                                ...invalidControlAria(outputFrame)
                            })
                        ]
                    }),
                    isTask && /*#__PURE__*/ _jsxs(FieldIssueFrame, {
                        ...frameFor("successors"),
                        children: [
                            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                icon: ArrowRight,
                                children: t("processFlow.successors")
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                role: "button",
                                tabIndex: 0,
                                onClick: ()=>setShowSuccessorDialog(true),
                                onKeyDown: (e)=>{
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setShowSuccessorDialog(true);
                                    }
                                },
                                className: "group relative bg-surface-subtle hover:bg-muted rounded-md p-2 space-y-1 text-sm transition-colors",
                                children: [
                                    /*#__PURE__*/ _jsx(Pencil, {
                                        className: "absolute top-2 right-2 w-3 h-3 text-fg-subtle opacity-0 group-hover:opacity-100"
                                    }),
                                    successorValue.persons.length === 0 && successorValue.orgFunctions.length === 0 && successorValue.systems.length === 0 && successorValue.externalContacts.length === 0 && !successorValue.successorStatus ? /*#__PURE__*/ _jsx("span", {
                                        className: "text-fg-subtle italic text-xs",
                                        children: t("processFlow.editable.noSuccessors")
                                    }) : /*#__PURE__*/ _jsx(SuccessorSummary, {
                                        value: successorValue,
                                        assigneeLookup: assigneeLookup,
                                        t: t,
                                        onRemove: handleRemoveSuccessor,
                                        onClearStatus: handleClearSuccessorStatus,
                                        suggestionInfoById: suggestionInfoById
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "ghost",
                                size: "small",
                                className: "mt-1.5 w-full h-7 text-muted-foreground hover:text-fg-secondary hover:bg-muted",
                                onClick: ()=>setShowSuccessorDialog(true),
                                "data-field-issue-focus": "",
                                children: [
                                    /*#__PURE__*/ _jsx(Plus, {
                                        className: "w-3.5 h-3.5 mr-1"
                                    }),
                                    t("processFlow.editable.addSuccessor")
                                ]
                            })
                        ]
                    })
                ]
            }),
            renderSuccessorPicker?.({
                open: showSuccessorDialog,
                onOpenChange: setShowSuccessorDialog,
                value: successorValue,
                onSave: handleSuccessorSubmit,
                type: "task",
                excludeIds: excludeIdsOf(successorValue)
            }),
            renderSuccessorPicker?.({
                open: showAssigneeDialog,
                onOpenChange: setShowAssigneeDialog,
                value: EMPTY_SUCCESSOR_VALUE,
                onSave: handleAssigneeSubmit,
                type: "assignee"
            }),
            renderSuccessorPicker?.({
                open: editingRuleId !== null,
                onOpenChange: (open)=>{
                    if (!open) setEditingRuleId(null);
                },
                value: editingRuleSuccessorValue,
                onSave: handleRuleSuccessorSubmit,
                type: "task",
                excludeIds: excludeIdsOf(editingRuleSuccessorValue)
            }),
            showDecisionTableModal && /*#__PURE__*/ _jsx(DecisionTableDialog, {
                onClose: ()=>setShowDecisionTableModal(false),
                onSave: handleSaveDecisionRules,
                // Display rules carry populated successors; the dialog passes them
                // through verbatim, so the unpopulated cast matches the patch paths
                // above (see handleRuleSuccessorSubmit / handleRemoveRuleSuccessor).
                initialData: step.rules ?? []
            })
        ]
    });
} // ---------------------------------------------------------------------------
 // Sub-components extracted for clarity
 // ---------------------------------------------------------------------------

//# sourceMappingURL=EditableStepDetailContent.js.map